import { useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';

import { useFDFQuoteVariant_buyer$key } from './__generated__/useFDFQuoteVariant_buyer.graphql';
import { useFDFQuoteVariant_item$key } from './__generated__/useFDFQuoteVariant_item.graphql';
import {
    useFDFQuoteVariant_relevantShipping$key,
    useFDFQuoteVariant_relevantShipping$data,
} from './__generated__/useFDFQuoteVariant_relevantShipping.graphql';
import { useFDFQuoteVariant_viewer$key } from './__generated__/useFDFQuoteVariant_viewer.graphql';

import { getUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';

type ShippingQuotes = NonNullable<useFDFQuoteVariant_relevantShipping$data['shipmentQuotes']>;

const get1stDibsFDFQuote = (shippingQuotes: ShippingQuotes): ShippingQuotes[number] | undefined => {
    return shippingQuotes.find(
        quote =>
            quote?.isFromInternal &&
            quote?.serviceMethod?.isFreight &&
            quote?.serviceMethod?.serviceType === 'AIR_FREIGHT'
    );
};

const buyerFragment = graphql`
    fragment useFDFQuoteVariant_buyer on User {
        addressBook(partialAddressCheckoutEligibility: $partialAddressCheckoutEligibility) {
            proAddress {
                country
            }
            personalAddress {
                country
            }
            addressList {
                country
            }
        }
    }
`;

const itemFragment = graphql`
    fragment useFDFQuoteVariant_item on Item {
        address {
            country
        }
        shipmentQuotes {
            serviceMethod {
                serviceType
                isFreight
            }
            isFromDealer
            isFromInternal
            isShipmentSubsidized
        }
    }
`;

const relevantShippingFragment = graphql`
    fragment useFDFQuoteVariant_relevantShipping on RelevantShippingInfo {
        shipmentQuotes {
            serviceMethod {
                serviceType
                isFreight
            }
            isFromDealer
            isFromInternal
            isShipmentSubsidized
        }
    }
`;

const viewerFragment = graphql`
    fragment useFDFQuoteVariant_viewer on Viewer {
        regionalInfo {
            countryCode
        }
    }
`;

export const useFDFQuoteVariant = ({
    buyer: buyerRef,
    item: itemRef,
    viewer: viewerRef,
    relevantShipping: relevantShippingRef,
}: {
    buyer: useFDFQuoteVariant_buyer$key | null;
    item: useFDFQuoteVariant_item$key | null;
    viewer: useFDFQuoteVariant_viewer$key | null;
    relevantShipping: useFDFQuoteVariant_relevantShipping$key | null;
}): void => {
    const buyer = useFragment(buyerFragment, buyerRef);
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const relevantShipping = useFragment(relevantShippingFragment, relevantShippingRef);

    const shippingOptions = relevantShipping?.shipmentQuotes || [];
    const { addressBook } = buyer || {};
    const { proAddress, personalAddress, addressList } = addressBook || {};
    const shipToAddress = proAddress || personalAddress || addressList?.[0];
    const { countryCode } = getUserGeoInfo() || {};
    const userCountry = countryCode || shipToAddress?.country;
    //Take regional info country code in consideration.
    //If buyer is located in US - test tracking should not fire.
    const regionalInfoCountryCode = viewer?.regionalInfo?.[0]?.countryCode;

    const itemQuotes = item?.shipmentQuotes || [];
    const itemLocation = item?.address?.country;
    const hasParcelQuotes = shippingOptions.some(
        quote => quote?.serviceMethod?.serviceType === 'PARCEL'
    );
    const hasSellerQuote = shippingOptions.some(
        quote => quote?.isFromDealer && quote?.serviceMethod?.serviceType
    );

    const dibsFDFQuote = get1stDibsFDFQuote(shippingOptions);
    const dibsFDFQuoteOnItem = get1stDibsFDFQuote(itemQuotes);
    const isDibsFDFQuoteSubsidised =
        dibsFDFQuote?.isShipmentSubsidized || dibsFDFQuoteOnItem?.isShipmentSubsidized;

    const fdfAbQuoteTest = getAbTestV2('fdf-us-row-test-q42023');
    const isFDFQuoteTestVariant = fdfAbQuoteTest?.variant === 'variant';
    const isFDFQuoteTestControl = fdfAbQuoteTest?.variant === 'control';

    const isFdfQuoteABTestEligible = !!(
        itemLocation === 'US' &&
        userCountry &&
        userCountry !== 'US' &&
        regionalInfoCountryCode &&
        regionalInfoCountryCode !== 'US' &&
        !hasParcelQuotes &&
        !hasSellerQuote &&
        !isDibsFDFQuoteSubsidised &&
        ((isFDFQuoteTestVariant && dibsFDFQuote) ||
            (isFDFQuoteTestControl && dibsFDFQuoteOnItem && !dibsFDFQuote))
    );

    useEffect(() => {
        if (isFdfQuoteABTestEligible) {
            trackAbTestV2Variant('fdf-us-row-test-q42023');
        }
    }, [isFdfQuoteABTestEligible]);
};
